import { useState } from "react";
import { Button, Modal, NumberInput, TextArea } from "@a2z/ui";
import { useTranslate } from "@/lib/i18n";
import { useMoney } from "@/shared/ui";
import type { MenuItem } from "@/lib/api/types";
import {
  UnsavedChangesPrompt,
  useDiscardGuard,
} from "@/shared/UnsavedChangesPrompt";

/**
 * The amount prompt behind "سعر مختلف" (D46, S9).
 *
 * Reachable only for a session holding `RESTAURANT_INVOICE_PRICE_OVERRIDE` —
 * the caller never renders the tile control otherwise, so this dialog opening
 * at all already means the permission check has passed. The amount replaces
 * the catalogue LIST price on the `addItems` line; the invoice's tier discount
 * (if any) still applies on top of it, which is why the number typed here is
 * not necessarily the number the plate is charged at.
 *
 * That last sentence used to be true only in this comment. On a bill opened
 * inside a happy hour a manager typed 200 and the guest was charged 160, with
 * nothing on screen saying so — the dialog showed one number and the bill
 * showed another, and the person who has to defend the price at the table was
 * the one not told. So the charged figure is now STATED whenever a tier is on
 * the bill, computed with the same rule the server prices by
 * (`resolveUnitChargedPrice`: list × (1 − pct/100), rounded to the piastre).
 * On an untiered bill — the common case — the two numbers are equal and the
 * line is absent rather than restating the amount above it.
 */
export function PriceOverrideDialog({
  item,
  discountPercent,
  isSubmitting,
  onCancel,
  onConfirm,
}: {
  item: MenuItem;
  /**
   * The tier concession this bill was opened on, 0 when it carries none.
   * Passed in rather than read here: the invoice screen already resolves it
   * for the totals block and the menu board, and a second resolution is a
   * second chance to disagree with the price the server will charge.
   */
  discountPercent: number;
  isSubmitting: boolean;
  onCancel: () => void;
  /** The amount AND why — the server refuses one without the other. */
  onConfirm: (unitPrice: number, note: string) => void | Promise<void>;
}) {
  const t = useTranslate();
  const money = useMoney();
  const [amount, setAmount] = useState(String(item.price));
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [noteError, setNoteError] = useState<string | null>(null);

  /**
   * What the guest will actually pay for one of these, or `null` when there
   * is nothing to disclose (no tier, or an amount that is not yet a number).
   * Deliberately not shown as an error state: a half-typed "1" is a person
   * mid-keystroke, not a mistake to shout about.
   */
  const parsedAmount = Number(amount);
  const chargedPrice =
    discountPercent > 0 && Number.isFinite(parsedAmount) && parsedAmount >= 0
      ? Math.round(parsedAmount * (1 - discountPercent / 100) * 100) / 100
      : null;

  const submit = () => {
    const parsed = Number(amount);
    if (!Number.isFinite(parsed) || parsed < 0) {
      setError(t((m) => m.pos.priceOverrideInvalid));
      return;
    }
    setError(null);
    /*
     * The reason is REQUIRED (founder decision, 2026-09-03). Checked here as
     * well as on the server so the cashier is told at the moment they can fix
     * it, rather than after a round trip that refuses the whole line — and
     * trimmed, because a space bar is how a required field gets defeated.
     */
    const reason = note.trim();
    if (reason.length === 0) {
      setNoteError(t((m) => m.pos.priceOverrideNoteRequired));
      return;
    }
    setNoteError(null);
    void onConfirm(Math.round(parsed * 100) / 100, reason);
  };

  /*
   * An amount moved off the list price, or a reason begun. The reason is the
   * only record of WHY a plate was discounted — every roll-up afterwards reads
   * the override as the list price — so losing it to a swipe loses the audit.
   */
  const guard = useDiscardGuard(
    amount !== String(item.price) || note.trim() !== "",
    onCancel,
  );

  return (
    <>
    <Modal
      isOpen
      onClose={guard.requestClose}
      size="sm"
      title={t((m) => m.pos.priceOverrideDialogTitle)}
      subtitle={item.nameAr}
      closeLabel={t((m) => m.common.close)}
      data-testid="price-override-dialog"
      footerActions={
        <>
          <Button variant="outline" size="touch" onClick={guard.requestClose}>
            {t((m) => m.common.cancel)}
          </Button>
          <Button
            variant="primary"
            size="touch"
            className="font-extrabold"
            isLoading={isSubmitting}
            data-testid="price-override-confirm"
            onClick={submit}
          >
            {t((m) => m.pos.priceOverrideConfirm)}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <label
          htmlFor="price-override-amount"
          className="text-[length:var(--font-size-2xs)] font-extrabold text-[color:var(--text-secondary)]"
        >
          {t((m) => m.pos.priceOverrideAmountLabel)}
        </label>
        <NumberInput
          id="price-override-amount"
          name="priceOverrideAmount"
          value={amount}
          allowDecimal
          min={0}
          step={0.01}
          dir="ltr"
          hasError={Boolean(error)}
          onChange={(event) => {
            setAmount(event.target.value);
            setError(null);
          }}
          data-testid="price-override-amount"
        />
        {error ? (
          <p
            role="alert"
            className="text-xs font-medium text-[color:var(--danger)]"
          >
            {error}
          </p>
        ) : null}
        {/* The bill's tier still comes off this number. Stated as the CHARGED
            figure rather than as a rule about percentages, because the thing
            the manager needs to check before tapping confirm is what the
            guest pays, not the arithmetic that got there. */}
        {chargedPrice !== null ? (
          <p
            className="rounded-[10px] border border-[color:var(--border-light)] bg-[color:var(--surface-alt)] px-3 py-2 text-[length:calc(var(--font-size-2xs)_+_0.5px)] font-semibold text-[color:var(--text-secondary)]"
            data-testid="price-override-charged"
          >
            {t((m) => m.pos.priceOverrideTierNote, {
              percent: discountPercent,
              price: money(chargedPrice),
            })}
          </p>
        ) : null}
        {/* WHY this price. Required, and asked here rather than inferred later:
            a void of one tea has demanded a reason since D43, and an override
            is the harder of the two to see afterwards — the amount replaces
            the LIST price, so every roll-up reads a discount of zero. Free
            text, not a code list: the reasons a manager discounts a plate are
            not a closed set, and a wrong code picked to get past a dialog is
            worse evidence than a sentence. */}
        {/* The label WRAPS the control rather than pointing at it: the shared
            `TextArea` renders no `id`, so a `htmlFor` would associate with
            nothing and the field would be unlabelled for a screen reader. */}
        <label className="flex flex-col gap-3 text-[length:var(--font-size-2xs)] font-extrabold text-[color:var(--text-secondary)]">
          {t((m) => m.pos.priceOverrideNoteLabel)}
          <TextArea
            name="priceOverrideNote"
            value={note}
            rows={2}
            maxLength={200}
            hasError={Boolean(noteError)}
            placeholder={t((m) => m.pos.priceOverrideNotePlaceholder)}
            onChange={(event) => {
              setNote(event.target.value);
              setNoteError(null);
            }}
          />
        </label>
        {noteError ? (
          <p
            role="alert"
            className="text-xs font-medium text-[color:var(--danger)]"
          >
            {noteError}
          </p>
        ) : null}
      </div>
    </Modal>
    <UnsavedChangesPrompt
      isOpen={guard.asking}
      onDiscard={guard.discard}
      onKeepEditing={guard.keepEditing}
      testId="price-override-discard-prompt"
    />
    </>
  );
}
